import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

// Utility function to format timestamps in user's local timezone
const formatLocalDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    let date;
    
    // Handle timestamps without timezone (assume they are UTC)
    if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
      // For timestamps without timezone info, assume UTC and convert
      date = new Date(timestamp + 'Z');
    } else {
      date = new Date(timestamp);
    }
    
    // Ensure the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', timestamp);
      return 'Invalid Date';
    }
    
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Format Error';
  }
};

const KitchenDisplay = () => {
  const { restaurantId } = useParams();
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true); // Toggle for 60-second refresh
  const [lastUpdated, setLastUpdated] = useState(null); // Last updated timestamp
  const [tables, setTables] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const intervalRef = useRef(null);
  
  // Toggle auto-refresh on/off
  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);
  };

  const orderStatuses = [
    { value: '', label: 'All Orders' },
    { value: 'received', label: 'Received' },
    { value: 'preparing', label: 'Preparing' },
    { value: 'ready', label: 'Ready' }
  ];

  // Optional 60-second auto-refresh with toggle control
  useEffect(() => {
    fetchOrders();
    
    // Set up 60-second auto-refresh if enabled
    if (autoRefreshEnabled) {
      intervalRef.current = setInterval(() => {
        console.log('🔄 Kitchen Display auto-refresh (60s interval)');
        fetchOrders();
      }, 60000); // 60 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [restaurantId, autoRefreshEnabled]);

  const fetchOrders = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      // Get restaurant info (public endpoint)
      if (!restaurant) {
        try {
          const restaurantResponse = await axios.get(`${backendUrl}/api/restaurants/${restaurantId}`);
          setRestaurant(restaurantResponse.data);
        } catch (error) {
          console.error('Error fetching restaurant info:', error);
          toast.error('Failed to load restaurant information');
          return;
        }
      }

      // Get orders (public endpoint for kitchen display)
      const ordersResponse = await axios.get(`${backendUrl}/api/restaurants/${restaurantId}/kitchen-orders`);
      
      // Sort orders by latest received/modified (descending)
      const sortedOrders = ordersResponse.data.sort((a, b) => {
        const aTime = Math.max(
          new Date(a.created_at).getTime(),
          new Date(a.updated_at || a.created_at).getTime()
        );
        const bTime = Math.max(
          new Date(b.created_at).getTime(),
          new Date(b.updated_at || b.created_at).getTime()
        );
        return bTime - aTime; // Descending order (latest first)
      });
      
      setAllOrders(sortedOrders);
      
      // Extract unique tables from orders for filtering
      const uniqueTables = [...new Set(sortedOrders
        .map(order => order.table_info?.table_number)
        .filter(tableNumber => tableNumber)
      )].map(tableNumber => ({
        id: tableNumber,
        table_number: tableNumber
      }));
      
      setTables(uniqueTables);
      
      // Apply table filter
      if (selectedTable) {
        const filteredOrders = sortedOrders.filter(order => 
          order.table_info?.table_number?.toString() === selectedTable
        );
        setOrders(filteredOrders);
      } else {
        setOrders(sortedOrders);
      }
      
      setLastUpdate(new Date());
      setLastUpdated(new Date()); // Update last updated timestamp
    } catch (error) {
      console.error('Error fetching kitchen data:', error);
      if (error.response?.status === 404) {
        toast.error('Restaurant not found');
      } else {
        toast.error('Failed to load kitchen orders. Please check the restaurant ID.');
      }
    }
  };

  // Play notification sound
  const playNotificationSound = (type = 'default') => {
    try {
      // Create audio context for different notification sounds
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create different tones for different notification types
      const frequencies = {
        'success': [800, 1000, 1200], // New order - ascending tones
        'warning': [600, 400, 600],   // Modified order - attention pattern
        'info': [500, 700]            // General notification
      };
      
      const freqs = frequencies[type] || frequencies['info'];
      
      freqs.forEach((frequency, index) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.3);
        }, index * 150);
      });
    } catch (error) {
      // Fallback to system beep if Web Audio API fails
      console.log('Audio notification failed, using fallback');
    }
  };

  // Add notification for new/modified orders
  const addNotification = (message, type = 'info', playSound = true) => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    setNotifications(prev => [notification, ...prev.slice(0, 4)]); // Keep last 5 notifications
    
    // Play notification sound if enabled
    if (playSound && soundEnabled) {
      playNotificationSound(type);
    }
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Update orders when filters change
  useEffect(() => {
    let filteredOrders = allOrders;
    
    // Apply table filter
    if (selectedTable) {
      filteredOrders = filteredOrders.filter(order => 
        order.table_info?.table_number?.toString() === selectedTable
      );
    }
    
    // Apply status filter
    if (selectedStatus) {
      filteredOrders = filteredOrders.filter(order => 
        order.order_status === selectedStatus
      );
    }
    
    // Check for new or modified orders (for notifications)
    if (orders.length > 0) { // Only check after initial load
      const currentOrdersMap = new Map(orders.map(o => [o.id, o]));
      
      filteredOrders.forEach(order => {
        const existingOrder = currentOrdersMap.get(order.id);
        
        if (!existingOrder) {
          // New order
          addNotification(
            `🔔 New order: Table ${order.table_info?.table_number || 'Unknown'}`, 
            'success'
          );
        } else if (
          existingOrder.order_status !== order.order_status ||
          existingOrder.updated_at !== order.updated_at
        ) {
          // Modified order
          addNotification(
            `📝 Order updated: Table ${order.table_info?.table_number || 'Unknown'} - ${order.order_status}`, 
            'warning'
          );
        }
      });
    }
    
    setOrders(filteredOrders);
  }, [selectedTable, selectedStatus, allOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      await axios.put(`${backendUrl}/api/orders/${orderId}/status`, {
        order_status: newStatus
      });
      
      toast.success(`Order marked as ${newStatus}!`);
      fetchOrders(); // Refresh orders immediately
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'received': return 'bg-blue-100 text-blue-800';
      case 'preparing': return 'bg-yellow-100 text-yellow-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrderPriority = (order) => {
    const now = new Date();
    const orderTime = new Date(order.created_at);
    const minutesAgo = Math.floor((now - orderTime) / (1000 * 60));
    
    if (minutesAgo > 15) return 'urgent';
    if (minutesAgo > 10) return 'warning';
    return 'normal';
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-red-500 bg-red-50';
      case 'warning': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-gray-200 bg-white';
    }
  };

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading kitchen display...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
        <style jsx>{`
          @keyframes slide-in-right {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out forwards;
          }
        `}</style>
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title Section */}
          <div className="py-4 border-b border-gray-100 lg:border-0 lg:pb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              🍳 {restaurant.name} - Kitchen Display
            </h1>
            <p className="text-gray-600 text-sm lg:text-base mt-1">
              Active Orders: {orders.length} • Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          </div>

          {/* Mobile-Responsive Controls */}
          <div className="py-4 space-y-4 lg:space-y-0">
            {/* First Row: Filters */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1 lg:hidden">
                  Filter by Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="form-input w-full"
                >
                  {orderStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-1 lg:hidden">
                  Filter by Table
                </label>
                <select
                  value={selectedTable}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  className="form-input w-full"
                >
                  <option value="">All Tables</option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.table_number}>
                      Table {table.table_number}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Second Row: Actions and Status */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
              <div className="flex items-center justify-center sm:justify-start space-x-4 text-sm text-gray-600 order-2 sm:order-1">
                {/* Auto-Refresh Toggle */}
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border">
                  <input
                    type="checkbox"
                    id="kitchen-auto-refresh-toggle"
                    checked={autoRefreshEnabled}
                    onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="kitchen-auto-refresh-toggle" className="cursor-pointer">
                    Auto-refresh (1 min)
                  </label>
                </div>
                
                {/* Last Updated Display */}
                {lastUpdated && (
                  <div className="text-xs text-gray-500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </div>
                )}
                
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                    soundEnabled 
                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>{soundEnabled ? '🔊' : '🔇'}</span>
                  <span>{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
                </button>
              </div>
              
              <button
                onClick={fetchOrders}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium order-1 sm:order-2"
              >
                <span>🔄</span>
                <span>Refresh Now</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-4 rounded-lg shadow-lg border-l-4 animate-slide-in-right ${
                notification.type === 'success'
                  ? 'bg-green-50 border-green-400 text-green-700'
                  : notification.type === 'warning'
                  ? 'bg-orange-50 border-orange-400 text-orange-700'
                  : 'bg-blue-50 border-blue-400 text-blue-700'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-sm font-medium">{notification.message}</p>
                  <p className="text-xs opacity-75 mt-1">
                    {notification.timestamp.toLocaleTimeString()}
                  </p>
                </div>
                <button
                  onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Orders Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🍽️</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Active Orders</h3>
            <p className="text-gray-600">New orders will appear here automatically</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orders.map((order) => {
              const priority = getOrderPriority(order);
              return (
                <div
                  key={order.id}
                  className={`border-2 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow ${getPriorityClass(priority)}`}
                >
                  {/* Order Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Table {order.table_info?.table_number || 'N/A'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Order #{order.id?.slice(-8) || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatLocalDateTime(order.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(order.order_status)}`}>
                        {order.order_status.toUpperCase()}
                      </span>
                      {priority === 'urgent' && (
                        <div className="text-red-600 text-xs mt-1 font-medium">
                          🚨 URGENT
                        </div>
                      )}
                      {priority === 'warning' && (
                        <div className="text-yellow-600 text-xs mt-1 font-medium">
                          ⚠️ PRIORITY
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Info */}
                  {(order.customer_name || order.customer_phone) && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      {order.customer_name && (
                        <p className="text-sm font-medium text-gray-700">
                          👤 {order.customer_name}
                        </p>
                      )}
                      {order.customer_phone && (
                        <p className="text-sm text-gray-600">
                          📞 {order.customer_phone}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-3 text-lg">Items to Prepare:</h4>
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-start">
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">
                              {item.quantity}x {item.menu_item_name}
                            </span>
                            {item.notes && (
                              <div className="text-sm text-red-600 mt-1 bg-red-50 p-2 rounded border-l-4 border-red-400">
                                <strong>⚠️ Special Request:</strong> {item.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Special Notes */}
                  {order.notes && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm font-medium text-red-800 mb-1">
                        🚨 Special Instructions:
                      </p>
                      <p className="text-sm text-red-700">{order.notes}</p>
                    </div>
                  )}

                  {/* Status Update Buttons */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {order.order_status !== 'preparing' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'preparing')}
                        disabled={loading}
                        className="flex-1 px-3 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 font-medium"
                      >
                        👨‍🍳 Mark Preparing
                      </button>
                    )}
                    {order.order_status !== 'ready' && order.order_status !== 'completed' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'ready')}
                        disabled={loading}
                        className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        ✅ Mark Ready
                      </button>
                    )}
                    {order.order_status !== 'completed' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'completed')}
                        disabled={loading}
                        className="flex-1 px-3 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 font-medium"
                      >
                        🏁 Mark Completed
                      </button>
                    )}
                  </div>

                  {/* Kitchen Focus - Payment info removed as not relevant for kitchen staff */}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white border-t mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <div>
              Kitchen Display • Auto-refresh every 60 seconds (optional)
            </div>
            <div>
              {restaurant?.name} • Powered by Restaurant QR System
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KitchenDisplay;